import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CompactSelectPicker from '../components/ui/CompactSelectPicker';
import { Row, Section } from '../components/settingsPrimitives';
import { useExtensionStore } from '../extensions/store';
import { ROLES, availableTools, hiddenForRole } from './rolePresets';
import { withoutApiClientTools } from '../extensions/apiClientSwitch';
import { markOnboardingComplete, readOnboarding } from './onboardingState';
import ToolList from './ToolList';
import './onboarding.css';

const EMPTY = [];

/**
 * Settings → Appearance → "Your tools": the onboarding's tool list, to change
 * the rail later. Picking a role resets the list to that role's picks; the
 * switches then fine-tune it. Same store, same rules (one tool always stays).
 */
export default function YourToolsSection() {
  const { t } = useTranslation();
  // While the API Client is switched off (Settings → Extensions) it and
  // History are not tools to show or hide (extensions/apiClientSwitch.js).
  const apiClientEnabled = useExtensionStore((s) => s.apiClientEnabled);
  const tools = useMemo(() => {
    const keep = new Set(withoutApiClientTools(availableTools().map((tool) => tool.id), apiClientEnabled));
    return availableTools().filter((tool) => keep.has(tool.id));
  }, [apiClientEnabled]);
  const availableIds = useMemo(() => tools.map((tool) => tool.id), [tools]);
  const hidden = useExtensionStore((s) => s.railHidden) || EMPTY;
  const setRailHidden = useExtensionStore((s) => s.setRailHidden);
  const toggleRailHidden = useExtensionStore((s) => s.toggleRailHidden);
  const [roleId, setRoleId] = useState(() => readOnboarding()?.role || 'custom');

  const pickRole = (id) => {
    setRoleId(id);
    setRailHidden(hiddenForRole(id), availableIds);
    // Remember the new role for the next replay; keeps the tour completed.
    markOnboardingComplete(id);
  };

  return (
    <Section title={t('onboarding.settings.title')}>
      <Row label={t('onboarding.settings.role')} desc={t('onboarding.settings.roleDesc')}>
        <CompactSelectPicker
          value={roleId}
          onChange={pickRole}
          options={ROLES.map((role) => ({ value: role.id, label: t(`onboarding.roles.${role.id}.name`) }))}
          aria-label={t('onboarding.settings.role')}
        />
      </Row>
      <p className="settings-row-desc ob-settings-desc">{t('onboarding.settings.desc')}</p>
      <div className="ob-vars ob-settings-list">
        <ToolList roleId={roleId} tools={tools} hidden={hidden} onToggle={(id) => toggleRailHidden(id, availableIds)} />
      </div>
    </Section>
  );
}
